import dayjs from 'dayjs';

import {
    MEMBERSHIP_LIFECYCLE_ENDED_ERROR_CODE,
    MEMBERSHIP_CARRIED_OUT_MESSAGE,
    MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE,
    REFUND_ROW_IMMUTABLE_ERROR_CODE,
    REFUND_ROW_IMMUTABLE_MESSAGE,
    WITHDRAWN_MEMBER_READ_ONLY_ERROR_CODE,
    WITHDRAWN_MEMBER_READ_ONLY_MESSAGE,
} from '../../lib/membershipLifecycle';

export type SharedScheduleFailure = {
    status?: number;
    data?: unknown;
    /** ky apiClient가 구조화 오류 본문을 보존하는 필드. */
    body?: unknown;
    /** ky apiClient가 서버 message로 채우는 Error.message. */
    message?: string;
    serverMessage?: string;
};

const readErrorCode = (data: unknown): string => {
    if (!data || typeof data !== 'object') return '';
    const errorCode = (data as { errorCode?: unknown }).errorCode;
    return typeof errorCode === 'string' ? errorCode.trim() : '';
};

const readDetails = (result: SharedScheduleFailure | null | undefined): Record<string, unknown> => {
    const data = result?.data ?? result?.body;
    if (!data || typeof data !== 'object') return {};
    const details = (data as { details?: unknown }).details;
    return details && typeof details === 'object' ? (details as Record<string, unknown>) : {};
};

const formatRefundCapacityDetails = (
    result: SharedScheduleFailure | null | undefined,
): string => {
    const details = readDetails(result);
    const rawValues = [
        details.usedCount,
        details.futureScheduledCount,
        details.pendingRequestCount,
        details.shortage,
    ];
    if (
        rawValues.some(
            (value) => typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0,
        )
    ) {
        return '';
    }
    const values = rawValues as number[];
    const [usedCount, futureScheduledCount, pendingRequestCount, shortage] = values;
    return ` (사용 ${usedCount}회 · 미래 ${futureScheduledCount}회 · 대기 ${pendingRequestCount}회 · 부족 ${shortage}회)`;
};

/**
 * MEMBERSHIP_CAPACITY_EXHAUSTED의 details.usage(사용/미래예약/대기)를 사람이 읽을 문구로 만든다.
 * formatRefundCapacityDetails와 같은 원칙 — 값이 하나라도 손상되면 숫자를 추측하지 않는다.
 */
const formatCapacityUsageDetails = (
    result: SharedScheduleFailure | null | undefined,
): string => {
    const usage = readDetails(result).usage;
    if (!usage || typeof usage !== 'object') return '';
    const usageRecord = usage as Record<string, unknown>;
    const rawValues = [
        usageRecord.usedCount,
        usageRecord.futureScheduledCount,
        usageRecord.pendingRequestCount,
    ];
    if (
        rawValues.some(
            (value) => typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0,
        )
    ) {
        return '';
    }
    const [usedCount, futureScheduledCount, pendingRequestCount] = rawValues as number[];
    return ` (사용 ${usedCount}회 · 미래 예약 ${futureScheduledCount}회 · 대기 ${pendingRequestCount}회)`;
};

/**
 * SHARED_MEMBERSHIP_DUPLICATE_SCHEDULE 의 details.duplicates 를 "누가·언제" 문구로 만든다.
 *
 * `materializedSchedules`(힌트)와 달리 이 배열은 서버가 **실제 조회로** 만든 것이라 믿어도
 * 된다(계약). 그래서 이름을 그대로 보여준다.
 *
 * `alias` 는 null 일 수 있다. 이름을 못 구하면 시각만, 둘 다 없으면 그 항목을 뺀다.
 * 항목이 많으면 앞의 몇 건만 보이고 나머지는 수로 줄인다 — 알럿이 길어지면 안 읽는다.
 */
const DUPLICATE_SCHEDULE_PREVIEW_LIMIT = 3;

const formatDuplicateScheduleDetails = (
    result: SharedScheduleFailure | null | undefined,
): string => {
    const duplicates = readDetails(result).duplicates;
    if (!Array.isArray(duplicates)) return '';

    const labels = duplicates
        .map((entry) => {
            if (!entry || typeof entry !== 'object') return '';
            const record = entry as { alias?: unknown; startTime?: unknown };
            const alias = typeof record.alias === 'string' ? record.alias.trim() : '';
            const startTime =
                typeof record.startTime === 'string' ? record.startTime.trim() : '';
            const when =
                startTime && dayjs(startTime).isValid()
                    ? dayjs(startTime).format('M월 D일 HH:mm')
                    : '';
            return [alias ? `${alias}님` : '', when].filter(Boolean).join(' ');
        })
        .filter((label) => label.length > 0);

    const unique = Array.from(new Set(labels));
    if (unique.length === 0) return '';

    const shown = unique.slice(0, DUPLICATE_SCHEDULE_PREVIEW_LIMIT);
    const restCount = unique.length - shown.length;
    return ` — ${shown.join(', ')}${restCount > 0 ? ` 외 ${restCount}건` : ''}`;
};

/**
 * SHARED_MEMBERSHIP_DUPLICATE_REQUEST_ITEM 의 details.duplicateItems 를 문구로 만든다.
 *
 * DB 중복(`duplicates`)과 **원인도 조치도 다르다.** 이쪽은 이번 요청 안에서 같은 회원이
 * 같은 시간에 두 번 이상 들어간 것이라, 고칠 곳이 캘린더가 아니라 **입력**이다.
 * 그래서 시각보다 **몇 번째 행이 겹쳤는지**(`indexes`)를 먼저 알린다.
 *
 * `duplicateItems` 도 서버가 실제로 판정한 것이라 믿어도 된다. 다만 `alias` 는 null 일 수
 * 있고, 손상 입력 방어 수준은 formatDuplicateScheduleDetails 와 같게 맞춘다.
 */
const formatDuplicateRequestItemDetails = (
    result: SharedScheduleFailure | null | undefined,
): string => {
    const duplicateItems = readDetails(result).duplicateItems;
    if (!Array.isArray(duplicateItems)) return '';

    const labels = duplicateItems
        .map((entry) => {
            if (!entry || typeof entry !== 'object') return '';
            const record = entry as { alias?: unknown; indexes?: unknown };
            const alias = typeof record.alias === 'string' ? record.alias.trim() : '';
            // 화면은 1부터 세는 행이라 0-based 인덱스를 그대로 보여주면 헷갈린다.
            const rows = Array.isArray(record.indexes)
                ? Array.from(
                      new Set(
                          record.indexes.filter(
                              (index): index is number =>
                                  typeof index === 'number' &&
                                  Number.isSafeInteger(index) &&
                                  index >= 0,
                          ),
                      ),
                  )
                      .sort((left, right) => left - right)
                      .map((index) => `${index + 1}번째`)
                : [];
            const rowText = rows.length > 0 ? `(${rows.join(', ')})` : '';
            return [alias ? `${alias}님` : '', rowText].filter(Boolean).join(' ');
        })
        .filter((label) => label.length > 0);

    const unique = Array.from(new Set(labels));
    if (unique.length === 0) return '';

    const shown = unique.slice(0, DUPLICATE_SCHEDULE_PREVIEW_LIMIT);
    const restCount = unique.length - shown.length;
    return ` — ${shown.join(', ')}${restCount > 0 ? ` 외 ${restCount}건` : ''}`;
};

export const getSharedScheduleErrorCode = (
    result: SharedScheduleFailure | null | undefined,
) => readErrorCode(result?.data ?? result?.body);

/**
 * 공유 수강권 일정 쓰기 실패만 사용자 행동으로 이어질 수 있는 문구로 변환한다.
 * 공유 오류가 아니면 null을 반환해 기존 일정 오류 처리를 그대로 사용한다.
 */
export const getSharedScheduleErrorMessage = (
    result: SharedScheduleFailure | null | undefined,
): string | null => {
    if (readDetails(result).context === 'CARRIED_OUT') {
        const lifecycleError = resolveMembershipLifecycleError(result);
        if (lifecycleError) return lifecycleError.message;
    }
    switch (getSharedScheduleErrorCode(result)) {
        case 'MEMBERSHIP_CAPACITY_EXHAUSTED':
            return `공유 수강권의 등록 가능 회차가 모두 소진되었습니다. 기존 일정을 취소하거나 다른 수강권을 선택해주세요.${formatCapacityUsageDetails(
                result,
            )}`;
        case 'MEMBERSHIP_USAGE_BUSY':
        case 'LEASE_LOST':
            return '다른 예약이 동시에 처리 중입니다. 잠시 후 같은 내용으로 다시 시도해주세요.';
        case 'MEMBERSHIP_ACCESS_DENIED':
        case 'SHARED_MEMBERSHIP_ACCESS_DENIED':
            return '이 회원은 선택한 공유 수강권을 사용할 권한이 없습니다. 참여자 상태를 확인해주세요.';
        case 'SHARED_MEMBER_RELATION_INACTIVE':
            return '회원과 담당 강사의 연결이 비활성 상태여서 공유 수강권을 사용할 수 없습니다. 회원 연결 상태를 확인해주세요.';
        case 'MEMBER_REQUEST_NOT_ALLOWED':
            return '현재 이 공유 수강권으로는 회원의 수업 신청을 받을 수 없습니다. 센터의 공유 신청 설정을 확인해주세요.';
        case 'SHARED_MEMBERSHIP_DISABLED':
            return '현재 센터에서는 공유 수강권 예약 기능을 사용할 수 없습니다.';
        case 'SHARE_PROVISIONING_INCOMPLETE':
            return '공유 수강권 설정이 아직 완료되지 않았습니다. 수강권 상세에서 설정 상태를 확인해주세요.';
        case 'SHARED_MEMBERSHIP_POOL_CHANGED':
        case 'SHARED_MEMBERSHIP_POOL_MISMATCH':
        case 'SHARED_MEMBERSHIP_NOT_USABLE':
        case 'SHARED_MEMBERSHIP_CONTEXT_MISMATCH':
            return '공유 수강권 정보가 변경되었습니다. 수강권 목록을 새로고침한 뒤 다시 시도해주세요.';
        case 'SHARED_MEMBERSHIP_OPERATION_MISMATCH':
            return '같은 예약 요청의 내용이 변경되었습니다. 화면의 입력값을 다시 확인한 뒤 새로 등록해주세요.';
        case 'SHARED_MEMBERSHIP_OPERATION_COMPENSATED':
            // 되돌림도, 재등록의 안전도 단정하지 않는다. 다중 풀에서는 이 오류가 나도 앞 풀
            // 일정이 남을 수 있고(계약 §배경), retryPolicy 가 NEW_KEY_REQUIRED 라 실제로
            // 새 키가 나간다. "다시 등록하면 안전하다"고 말하면 목록을 안 보고 재등록해
            // 중복 일정이 생긴다. 구버전(보상 취소 완료)에서도 참인 문장만 쓴다.
            return '같은 요청 번호로는 더 이상 처리할 수 없습니다. 일정 목록에서 등록 여부를 확인한 뒤 다시 시도해주세요.';
        case 'SHARED_MEMBERSHIP_OPERATION_NOT_MATERIALIZED':
            // 되돌렸다고 단정하지 않는다. 구버전 서버는 생성분을 보상 취소했지만 현행 서버는
            // 되돌리지 않는다. 두 서버 모두에서 참인 문장만 쓰고, 확인 경로를 알려준다.
            return '예약 결과를 확정하지 못했습니다. 일정 목록에서 등록 여부를 확인한 뒤 다시 시도해주세요.';
        case 'SHARED_MEMBERSHIP_INVALID_INPUT':
            return '공유 수강권 예약 요청 정보가 올바르지 않습니다. 입력값을 다시 확인해주세요.';
        case 'SHARED_MEMBERSHIP_REPLACEMENT_MISMATCH':
            return '변경하려는 일정과 공유 수강권 정보가 일치하지 않습니다.';
        case 'SHARED_MEMBERSHIP_TRANSFER_REQUIRES_DEDICATED_FLOW':
            return '공유 수강권으로 잡힌 수업은 수강권을 바꿀 수 없습니다. 일정을 취소하고 원하는 수강권으로 다시 등록해주세요.';
        case 'SCHEDULE_MEMBERSHIP_CHANGED':
            return '다른 곳에서 이 수업의 수강권이 먼저 변경되었습니다. 화면을 새로고침한 뒤 다시 확인해주세요.';
        case 'SHARED_SETTLEMENT_EFFECTIVE_MONTH_REQUIRED':
            return '공유 수강권 정산 적용 월이 설정되지 않았습니다. 센터의 배포 설정을 확인해주세요.';
        case 'SHARED_SETTLEMENT_PERIOD_LOCKED':
            return '정산 적용 월 이전의 수업에는 공유 수강권을 사용할 수 없습니다. 수업 일자를 확인해주세요.';
        case 'SHARED_MEMBERSHIP_MULTI_POOL_UNSUPPORTED':
            // 두 풀의 lease를 겹쳐 잡으면 교착이 생겨 서버가 막는다. 다음 행동을 바로 알린다.
            return '서로 다른 공유 수강권 두 개로는 한 번에 등록할 수 없습니다. 한 명은 개인 수강권을 선택해주세요.';
        case 'SHARED_MEMBERSHIP_DUPLICATE_SCHEDULE':
            // 서버가 같은 회원·같은 시각·같은 강사의 활성 일정을 실제로 조회해 거절한 것이다.
            // 한 건이라도 걸리면 아무것도 만들어지지 않으므로 "이미 있는 것"만 알리면 된다.
            // 같은 입력을 다시 보내면 같은 거절이 반복된다 — 기존 일정을 지우거나 시간을 바꿔야 한다.
            return `이미 등록된 수업이 있습니다${formatDuplicateScheduleDetails(
                result,
            )}\n기존 일정을 확인한 뒤 다시 등록해주세요.`;
        case 'SHARED_MEMBERSHIP_DUPLICATE_REQUEST_ITEM':
            // DB 중복(DUPLICATE_SCHEDULE)과 별개다. 이번 요청 안에서 같은 회원이 같은 시간에
            // 두 번 이상 들어갔다는 뜻이라, 캘린더가 아니라 **입력**을 고쳐야 한다.
            return `같은 회원이 같은 시간에 중복으로 선택되었습니다${formatDuplicateRequestItemDetails(
                result,
            )}\n회원 선택을 확인한 뒤 다시 등록해주세요.`;
        case 'SHARED_MEMBERSHIP_MEMBERSHIP_REQUIRED':
            return '어떤 수강권으로 등록할지 선택해주세요. 공유에 참여 중인 회원은 수강권을 지정해야 합니다.';
        case 'SHARED_MEMBERSHIP_MULTI_WRITE_UNSUPPORTED':
        case 'SHARED_MEMBERSHIP_SETTLEMENT_UNSUPPORTED':
            return '공유 수강권으로는 이 일정 등록 방식을 사용할 수 없습니다.';
        case 'SHARE_TEMPLATE_MEMBERSHIP_CONVERSION_NOT_ALLOWED':
            return '공유 상품으로 발급된 수강권은 기존 수강권 전환을 할 수 없습니다. 수강권 상세에서 공유 설정 상태를 확인해주세요.';
        case 'SHARE_PROVISIONED_POOL_MISMATCH':
            return '공유 발급 기록과 연결된 공유 풀 정보가 일치하지 않습니다. 자동으로 변경하지 말고 확인 코드를 포함해 운영 담당자에게 문의해주세요.';
        default:
            return null;
    }
};

/**
 * `retryPolicy` 를 아직 안 내려주는 **구버전 서버**용 폴백.
 *
 * 세 코드 모두 "같은 키가 영구히 막힌다"는 뜻이라, 키를 버리지 않으면 재시도가 영원히
 * 같은 오류로 되돌아온다. 신버전에서는 서버가 retryPolicy 로 직접 지시하므로 이 목록을
 * 보지 않는다.
 */
const DISCARD_SHARED_SCHEDULE_OPERATION_CODES = new Set([
    'SHARED_MEMBERSHIP_OPERATION_MISMATCH',
    'SHARED_MEMBERSHIP_OPERATION_COMPENSATED',
    'SHARED_MEMBERSHIP_OPERATION_NOT_MATERIALIZED',
]);

/**
 * 실패했지만 일정이 이미 만들어졌을 수 있는 상태.
 *
 * 1:2 등록은 실패해도 앞서 만들어진 일정을 되돌리지 않는다(개인 수강권 1:2가 원래 그랬던
 * 것과 같은 수준). 그래서 실패 응답이 "무엇이 남았는지"를 함께 내려준다.
 *
 * - `mayExist`: 서버가 준 `details.partialWriteMayExist`. **계약**이다.
 * - `memberIds` / `count`: `details.materializedSchedules` 에서 뽑은 **best-effort 힌트**다.
 *   안내 문구를 만드는 데만 쓰고, 무엇이 만들어졌는지 판정하는 근거로 삼지 않는다.
 *   판정은 언제나 일정 목록 재조회로 한다.
 *
 * 응답 자체를 못 받은 경우(status 0 — 타임아웃·네트워크 끊김)도 `mayExist: true`로 본다.
 * 그때가 오히려 서버조차 결과를 모르는 구간이라 "안 만들어졌다"고 단정할 수 없다.
 */
export type SharedSchedulePartialWrite = {
    /** 계약 필드(partialWriteMayExist) 또는 무응답으로만 결정된다. */
    mayExist: boolean;
    /**
     * materializedSchedules 가 알려준 회원 ID(중복 제거). 없으면 빈 배열.
     * **문구를 만드는 데만 쓴다.** 개수도 단정하지 않는다 — 힌트는 누락·과다가 가능하다.
     */
    memberIds: string[];
};

/**
 * 같은 operationId 로 다시 시도해도 되는지에 대한 서버의 명시적 지시(계약 §1-2).
 * 클라이언트가 스스로 추론하지 않는다.
 */
export type SharedScheduleRetryPolicy = 'SAME_KEY' | 'NEW_KEY_REQUIRED';

export const getSharedScheduleRetryPolicy = (
    result: SharedScheduleFailure | null | undefined,
): SharedScheduleRetryPolicy | null => {
    const retryPolicy = readDetails(result).retryPolicy;
    return retryPolicy === 'SAME_KEY' || retryPolicy === 'NEW_KEY_REQUIRED' ? retryPolicy : null;
};

const readMaterializedMemberIds = (details: Record<string, unknown>): string[] => {
    const materialized = details.materializedSchedules;
    if (!Array.isArray(materialized)) return [];
    const memberIds = materialized
        .map((entry) =>
            entry && typeof entry === 'object'
                ? String((entry as { memberId?: unknown }).memberId ?? '').trim()
                : '',
        )
        .filter((memberId) => memberId.length > 0);
    return Array.from(new Set(memberIds));
};

export const getSharedSchedulePartialWrite = (
    result: SharedScheduleFailure | null | undefined,
): SharedSchedulePartialWrite | null => {
    // 응답을 못 받았으면 결과를 알 수 없다. 서버가 처리 중이었을 수 있으므로 남은 것이
    // 있다고 보고 목록을 확인하게 한다.
    if (result?.status === 0) return { mayExist: true, memberIds: [] };

    const details = readDetails(result);

    // ⚠️ 판정은 계약 필드 하나로만 한다. materializedSchedules 는 누락·과다가 가능한
    // best-effort 힌트라 "남은 것이 있는가"의 근거가 될 수 없다. 힌트를 판정에 쓰면
    // 과다 보고 한 번이 shouldDiscardSharedScheduleOperation 을 통해 1:1·반복 경로의
    // operationId 까지 붙들어, 같은 입력 재시도가 영구히 막힌다.
    if (details.partialWriteMayExist !== true) return null;

    return { mayExist: true, memberIds: readMaterializedMemberIds(details) };
};

/**
 * 부분 성공 안내 문구. 원인 문구(getSharedScheduleErrorMessage) 뒤에 덧붙여 쓴다(계약 §2-3).
 *
 * 노출 여부는 계약 필드로만 정한다(getSharedSchedulePartialWrite).
 *
 * **무엇도 단정하지 않는다.** 힌트(materializedSchedules)는 누락·과다가 가능하므로
 * 개수도, "등록됐다"는 완료도 말하지 않는다. 이름을 보여줄 때조차 "~일 수 있습니다"로
 * 끝낸다. 확정된 사실은 언제나 일정 목록에서 사람이 확인한다.
 *
 * 이름은 호출부가 실제로 고른 회원 목록에서 되찾는다(resolveMemberName). 힌트에만 있는
 * 회원은 빈 문자열이 되어 걸러지므로, 화면이 모르는 사람 이름이 튀어나오지 않는다.
 */
export const getSharedSchedulePartialWriteNotice = (
    result: SharedScheduleFailure | null | undefined,
    options?: {
        /** memberId → 회원 이름. 못 찾으면 빈 문자열을 반환하면 된다. */
        resolveMemberName?: (memberId: string) => string;
    },
): string | null => {
    const partial = getSharedSchedulePartialWrite(result);
    if (!partial) return null;

    const names = Array.from(
        new Set(
            partial.memberIds
                .map((memberId) => (options?.resolveMemberName?.(memberId) || '').trim())
                .filter((name) => name.length > 0),
        ),
    );

    if (names.length === 0) {
        return CONFIRM_SCHEDULE_LIST_NOTICE;
    }

    return `${names.map((name) => `${name}님`).join(', ')}이 이미 등록됐을 수 있습니다\n일정 목록에서 확인해주세요.`;
};

/** 부분 성공 여부를 특정하지 못할 때 쓰는 기본 확인 안내. */
const CONFIRM_SCHEDULE_LIST_NOTICE =
    '일정이 이미 만들어졌을 수 있습니다\n일정 목록에서 확인해주세요.';

/**
 * 1:2 실패 뒤 "일정 목록을 확인하라"를 안내해야 하는가(계약 §1-4·§2-1·§2-2-1).
 *
 * **기본은 언제나 안내한다.** 예외는 서버가 명시적으로 부정한 경우 하나뿐이다.
 *
 * 상태코드로 "쓰기 전 실패"를 가르려던 시도는 **세 번 연속 틀렸다.**
 *  - 404: 일정을 다 만든 뒤 getAuthbyUserId('coach') 가 'Not found Auth' 로 던진다.
 *  - 401: 풀마다 도는 assertSharedMutationAuthorized 가 앞 풀이 쓴 뒤 던질 수 있다.
 *  - 400: 일정 생성 뒤 이어지는 DB 작업의 Mongoose 오류가 400 으로 올라올 수 있다.
 *
 * 셋 다 "이 코드는 쓰기 전이 확실하다"고 단정했다가 뒤집혔다. 컨트롤러가 일정을 만든 뒤에도
 * 조회·푸시·알림을 계속하고 그 구간의 어떤 오류든 계약 필드 없이 올라오므로, 상태코드만으로는
 * 쓰기 여부를 알 수 없다. 그래서 **상태코드 기반 제외를 없앴다.**
 * 잘못 안내하면 잡음이지만, 놓치면 이미 만들어진 일정을 모른 채 재등록해 중복이 생긴다.
 *
 * ⚠️ `retryPolicy` 로도 판단하지 않는다(계약 §2-2-1). `SAME_KEY` 는 "취소된 키가 없다"는
 * 뜻이지 "쓰기가 없었다"는 뜻이 아니다. 키 폐기는 shouldDiscardSharedScheduleOperation 이
 * 따로 판단한다 — 서로 다른 질문이고 서로의 근거가 될 수 없다.
 */
export const needsScheduleListConfirmation = (
    result: SharedScheduleFailure | null | undefined,
): boolean => {
    if (getSharedSchedulePartialWrite(result)?.mayExist) return true;
    // 서버가 "만들어진 것이 없다"고 명시한 경우만 생략한다. 추론이 아니라 서버의 진술이다.
    if (readDetails(result).partialWriteMayExist === false) return false;
    return true;
};

/**
 * 1:2 실패 뒤 원인 문구 뒤에 덧붙일 후속 안내. 화면은 이것만 부르면 된다.
 *
 * 부분 성공이 확인되면 누가 등록됐을 수 있는지까지 말하고, 특정하지 못하면 확인만 요청한다.
 * 쓰기 전 실패가 분명하면 아무것도 붙이지 않는다.
 */
export const getDuetFailureFollowUpNotice = (
    result: SharedScheduleFailure | null | undefined,
    options?: {
        resolveMemberName?: (memberId: string) => string;
        /**
         * 이 안내가 붙을 원인 문구. 원인 문구가 이미 목록 확인을 시키고 있으면
         * 일반 안내는 같은 말을 두 번 하게 되므로 붙이지 않는다. 누가 등록됐을 수 있는지
         * 알려주는 안내는 새 정보라 그대로 붙인다.
         */
        omitIfRedundantWith?: string;
    },
): string | null => {
    const partialNotice = getSharedSchedulePartialWriteNotice(result, options);
    const notice = partialNotice ?? (needsScheduleListConfirmation(result) ? CONFIRM_SCHEDULE_LIST_NOTICE : null);
    if (!notice) return null;

    const isGenericNotice = notice === CONFIRM_SCHEDULE_LIST_NOTICE;
    if (isGenericNotice && options?.omitIfRedundantWith?.includes('일정 목록')) return null;
    return notice;
};

/**
 * **단건 일정 쓰기**(1:1 · 추가 일정)의 공유 실패 중, 서버가 그 응답을 낼 시점에
 * **일정이 이미 만들어져 있을 수 있는** 코드.
 *
 * 단건 경로는 duplicable 과 달리 `partialWriteMayExist` · `retryPolicy` 를 응답에 싣지 않는다
 * (schedule.service.js 의 attachSharedDuplicableFailureDetails 는 duplicable 전용이다).
 * 그래서 `needsScheduleListConfirmation` 의 "기본 안내, 서버가 부정할 때만 생략" 규칙을 그대로
 * 쓰면 쓰기 전에 확정된 거절(회차 부족·권한·입력 오류)에까지 "이미 만들어졌을 수 있습니다"가
 * 붙어 서로 모순되는 안내가 된다. 단건은 반대로 **기본 생략, 아래 코드만 안내**한다.
 *
 * 판단 기준은 하나다 — **서버가 이 코드를 던지는 지점에 일정이 이미 있을 수 있는가.**
 *
 *  - `SHARED_MEMBERSHIP_OPERATION_MISMATCH`
 *    서버가 `findActiveScheduleByOperationKey` 로 **활성 일정을 찾은 뒤에만** 필드를 비교해
 *    던진다(schedule.service.js:4275-4279). 즉 일정이 있다는 것을 서버가 확인한 상태다.
 *    여기서 목록 확인을 빼면 직원이 이미 등록된 일정을 못 보고 하나 더 만든다.
 *  - `LEASE_LOST`
 *    writer 가 정상 종료한 **뒤에도** 소유권을 재확인하다가 나올 수 있다
 *    (sharedMembershipCapacity.service.ts:1813-1815). 그 뒤 보상 취소를 시도하지만 성공을
 *    보장하지 않는다. `MEMBERSHIP_USAGE_BUSY` 는 lease 획득 자체가 실패한 것이라
 *    쓰기 전이 확정이다 — 화면 문구는 같아도 성질이 다르므로 코드로 가른다.
 *  - `SHARED_MEMBERSHIP_OPERATION_COMPENSATED` · `..._NOT_MATERIALIZED`
 *    둘 다 결과를 단정할 수 없는 상태다. 지금은 원인 문구가 이미 목록 확인을 담고 있어
 *    `omitIfRedundantWith` 가 중복을 걸러내지만, 문구가 바뀌어도 안내가 빠지지 않도록
 *    기준에 따라 여기에 둔다.
 */
const POST_WRITE_SHARED_SCHEDULE_ERROR_CODES = new Set([
    'SHARED_MEMBERSHIP_OPERATION_MISMATCH',
    'LEASE_LOST',
    'SHARED_MEMBERSHIP_OPERATION_COMPENSATED',
    'SHARED_MEMBERSHIP_OPERATION_NOT_MATERIALIZED',
]);

export const isPostWriteSharedScheduleFailure = (
    result: SharedScheduleFailure | null | undefined,
): boolean => POST_WRITE_SHARED_SCHEDULE_ERROR_CODES.has(getSharedScheduleErrorCode(result));

/**
 * 다음 클릭에서 새 operationId 를 만들지 여부. **판단은 서버의 retryPolicy 로만 한다**(계약 §2-2).
 *
 * `partialWriteMayExist` 로 판단하면 안 된다. 풀 B 가 쓰기 후 실패해 서버가 보상 취소하면
 * 같은 키 재시도는 SHARED_MEMBERSHIP_OPERATION_COMPENSATED 로 영구 차단되는데, 그 응답에는
 * (풀 A 가 남아 있어서) partialWriteMayExist: true 가 함께 실린다. 그걸 근거로 키를 붙들면
 * 같은 오류가 영원히 반복된다 — 무한 루프.
 * `materializedSchedules` 는 애초에 힌트라 판정에 쓰지 않는다.
 *
 * 구버전 서버(retryPolicy 없음)는 기존 코드 기반 판정을 그대로 쓴다. 그 세 코드는 구버전에서
 * "같은 키 영구 차단"을 뜻하므로 폐기가 맞고, 이 폴백이 없으면 구버전에서 무한 루프가 된다.
 * 값이 있는데 아는 값이 아니면(미래 확장) 같은 폴백으로 내려간다 — 계약의 원칙이
 * "무한 루프보다 새 키가 낫다"이기 때문이다.
 *
 * 이 함수는 1:1·반복 경로도 함께 쓴다. 그 경로들은 retryPolicy 가 없어 폴백으로 처리되며,
 * 동작이 기존과 같다.
 */
export const shouldDiscardSharedScheduleOperation = (
    result: SharedScheduleFailure | null | undefined,
): boolean => {
    const retryPolicy = getSharedScheduleRetryPolicy(result);
    if (retryPolicy === 'NEW_KEY_REQUIRED') return true;
    if (retryPolicy === 'SAME_KEY') return false;
    return DISCARD_SHARED_SCHEDULE_OPERATION_CODES.has(getSharedScheduleErrorCode(result));
};

/** 공유 수강권 환불·업그레이드의 구조화 오류를 재시도/운영 조치 문구로 바꾼다. */
export const getSharedAccountingErrorMessage = (
    result: SharedScheduleFailure | null | undefined,
    fallback = '환불·업그레이드 처리에 실패했습니다.',
): string => {
    switch (getSharedScheduleErrorCode(result)) {
        case 'SHARED_MEMBERSHIP_REFUND_CAPACITY_CONFLICT':
            return `이미 사용되었거나 예약된 공용 회차보다 총 회차를 줄일 수 없습니다.${formatRefundCapacityDetails(
                result,
            )}`;
        case 'SHARED_MEMBERSHIP_REFUND_SOURCE_CHANGED':
        case 'SHARED_ACCOUNTING_POOL_STATE_CHANGED':
            return '공유 수강권 정보가 변경되었습니다. 새로고침한 뒤 같은 입력으로 다시 시도해주세요.';
        case 'SHARE_POOL_MEMBERSHIP_MISMATCH':
            return '공유 풀과 원본 수강권 정보가 일치하지 않습니다. 반복 처리하지 말고 운영 담당자에게 확인해주세요.';
        case 'SHARED_ACCOUNTING_OPERATION_IN_PROGRESS':
        case 'MEMBERSHIP_USAGE_BUSY':
        case 'LEASE_LOST':
            return '다른 예약 또는 회계 작업이 처리 중입니다. 잠시 후 입력값을 바꾸지 말고 다시 시도해주세요.';
        case 'SHARED_ACCOUNTING_REVIEW_REQUIRED':
            return '처리 결과를 자동으로 확정할 수 없습니다. 반복 처리하지 말고 운영 담당자에게 확인해주세요.';
        case 'SHARED_ACCOUNTING_PROVISIONING_INCOMPLETE':
            return '공유 수강권 설정이 아직 완료되지 않았습니다. 설정 상태를 복구한 뒤 같은 입력으로 다시 시도해주세요.';
        case 'SHARED_ACCOUNTING_PROVISIONING_REVIEW_REQUIRED':
            return '공유 수강권 설정에 운영 검토가 필요합니다. 환불·업그레이드를 반복하지 말고 운영 담당자에게 확인해주세요.';
        case 'SHARED_ACCOUNTING_ACCESS_DENIED':
            return '이 센터의 공유 수강권을 환불·업그레이드할 권한이 없습니다.';
        case 'SHARED_ACCOUNTING_OPERATION_PAYLOAD_MISMATCH':
        case 'MEMBER_TICKET_REFUND_OPERATION_PAYLOAD_MISMATCH':
            // 이 코드를 받으면 저장된 키를 폐기하므로(shouldDiscardSharedAccountingOperation)
            // 새로고침 없이 다시 제출하면 새 키로 나간다. 자동 재시도는 하지 않는다.
            return '이전 시도와 요청 내용이 달라 처리되지 않았습니다. 입력값을 확인한 뒤 다시 제출해주세요.';
        case 'SHARED_ACCOUNTING_OPERATION_ID_MISMATCH':
        case 'MEMBER_TICKET_REFUND_OPERATION_ID_MISMATCH':
            // 요청 번호 자체가 어긋난 경우다. 키를 버리면 같은 환불이 새 키로 다시 나갈 수 있어 폐기하지 않는다.
            return '같은 처리 번호의 입력값이 달라졌습니다. 화면을 새로고침한 뒤 다시 진행해주세요.';
        case 'SHARED_ACCOUNTING_OPERATION_ID_REQUIRED':
        case 'SHARED_ACCOUNTING_OPERATION_ID_INVALID':
            return '환불·업그레이드 요청 정보가 올바르지 않습니다. 회차, 금액, 일자를 확인해주세요.';
        case 'SHARED_ACCOUNTING_INVALID_INPUT':
            return result?.status === 409
                ? '공유 수강권의 센터 또는 원본 정보가 일치하지 않습니다. 반복 처리하지 말고 운영 담당자에게 확인해주세요.'
                : '환불·업그레이드 요청 정보가 올바르지 않습니다. 회차, 금액, 일자를 확인해주세요.';
        default:
            return fallback;
    }
};

/**
 * 서버 지문과 저장된 키가 어긋났다는 응답. 이 키로는 무엇을 보내도 통과할 수 없다.
 *
 * 프론트 키에 없는 필드가 서버 지문에 들어 있으면(예: 지문에 필드가 새로 추가되면)
 * 재제출이 영구히 409로 막힌다. 서버 원장에는 TTL이 없어 프론트 localStorage의 30일이
 * 지나야 저절로 풀리므로, 이 폐기가 교착을 즉시 해소하는 유일한 수단이다.
 *
 * 폐기만 하고 자동 재시도는 하지 않는다 — 새 키로 나가는 요청은 새 환불이므로
 * 관리자가 의도를 다시 확인하고 직접 눌러야 한다.
 *
 * `..._OPERATION_ID_MISMATCH`(요청 번호 자체가 갈린 경우)나 이미 환불된 수강권 409는
 * 여기 포함되지 않는다. 그 경우 키를 버리면 중복 환불을 막던 장치를 스스로 없애는 셈이다.
 */
const DISCARD_SHARED_ACCOUNTING_OPERATION_CODES = new Set([
    'SHARED_ACCOUNTING_OPERATION_PAYLOAD_MISMATCH',
    'MEMBER_TICKET_REFUND_OPERATION_PAYLOAD_MISMATCH',
    // 원매출 환불 원장은 지문에 reason 이 들어 있는데 프론트 키에는 없다(백엔드가 이미
    // 배포된 원장이라 지문 변경 시 기존 행이 무효화되므로 손대지 않았다). 지문이 어긋나도
    // 영구 교착이 아니라 일회성 에러로 내려가도록 여기서 폐기한다.
    'MEMBERSHIP_SALES_REFUND_OPERATION_PAYLOAD_MISMATCH',
]);

export const shouldDiscardSharedAccountingOperation = (
    result: SharedScheduleFailure | null | undefined,
): boolean => DISCARD_SHARED_ACCOUNTING_OPERATION_CODES.has(getSharedScheduleErrorCode(result));

/** 공유 풀 관리 실패 뒤 화면이 취해야 할 후속 동작. */
export type SharePoolAdminErrorAction =
    /** 서버 메시지만 보여주면 되는 경우. */
    | 'NONE'
    /** 풀을 다시 조회해 화면을 갱신한 뒤 재시도를 안내한다. */
    | 'REFETCH_POOL'
    /** details.retryable — 같은 입력으로 자동 1회 재시도한다. */
    | 'RETRY_ONCE'
    /** 이미 있는 활성 풀로 이동시킨다. sharePoolId를 함께 준다. */
    | 'OPEN_EXISTING_POOL'
    /** 해당 회원의 미래 예약을 먼저 정리하도록 안내한다. */
    | 'RESOLVE_FUTURE_USAGE'
    /** 소유 중인 공유 풀을 먼저 종료해야 한다. sourceMembershipIds를 함께 준다. */
    | 'CLOSE_OWNED_SHARE_POOLS'
    /** 참여 인원 한도에 걸렸다. 한도를 올려야 진행된다. */
    | 'RAISE_PARTICIPANT_LIMIT'
    /** 자동 재시도 금지. 운영 문의로 안내한다. */
    | 'CONTACT_SUPPORT'
    /** 종료된 수강권이다. 인계됐으면 현재 수강권으로 이동시킨다. currentMembershipId를 함께 준다. */
    | 'OPEN_CURRENT_MEMBERSHIP'
    /** 기능이 닫힌 센터. 애초에 버튼이 노출되지 않았어야 한다. */
    | 'HIDE_FEATURE';

export type SharePoolAdminError = {
    errorCode: string;
    message: string;
    action: SharePoolAdminErrorAction;
    /** OPEN_EXISTING_POOL일 때만 채워진다. */
    sharePoolId?: string;
    /** RESOLVE_FUTURE_USAGE일 때만 채워진다. */
    memberId?: string;
    /** CLOSE_OWNED_SHARE_POOLS일 때만 채워진다. 종료해야 할 공유의 원본 수강권들. */
    sourceMembershipIds?: string[];
    /** OPEN_CURRENT_MEMBERSHIP일 때만 채워진다. 인계로 이어진 현재 수강권. */
    currentMembershipId?: string;
};

/** MEMBERSHIP_NOT_SHAREABLE의 details.reason별 안내. */
const NOT_SHAREABLE_REASON_MESSAGES: Record<string, string> = {
    MEMBERSHIP_CAPACITY_OVERCOMMITTED:
        '이미 사용했거나 예약된 회차가 총 회차를 넘어 공유로 전환할 수 없습니다. 일정을 정리한 뒤 다시 시도해주세요.',
    MISSING_SCHEDULE_MEMBERSHIP_ID:
        '수강권이 지정되지 않은 일정이 있어 공유로 전환할 수 없습니다. 운영 담당자에게 확인해주세요.',
    SOURCE_MEMBERSHIP_USAGE_SCOPE_MISMATCH:
        '원구매자 범위 밖의 일정 또는 신청이 있어 공유로 전환할 수 없습니다. 운영 담당자에게 확인해주세요.',
    MEMBERSHIP_PERIOD_INVALID:
        '수강권의 기간 정보에 이상이 있어 공유로 전환할 수 없습니다. 운영 담당자에게 확인해주세요.',
    MEMBERSHIP_CAPACITY_INVALID:
        '수강권의 회차 정보에 이상이 있어 공유로 전환할 수 없습니다. 운영 담당자에게 확인해주세요.',
};

const readDetailString = (details: Record<string, unknown>, key: string): string => {
    const value = details[key];
    return typeof value === 'string' ? value.trim() : '';
};

const readDetailStringList = (details: Record<string, unknown>, key: string): string[] => {
    const value = details[key];
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0);
};

/** 서버가 내려준 한국어 메시지. 없으면 호출부 fallback을 쓴다. */
const readServerMessage = (result: SharedScheduleFailure | null | undefined): string => {
    const data = (result?.data ?? result?.body) as { message?: unknown } | null | undefined;
    const bodyMessage = typeof data?.message === 'string' ? data.message.trim() : '';
    if (bodyMessage) return bodyMessage;
    const serverMessage = typeof result?.serverMessage === 'string' ? result.serverMessage.trim() : '';
    if (serverMessage) return serverMessage;
    return typeof result?.message === 'string' ? result.message.trim() : '';
};

/**
 * 종료된 수강권·환불행·탈퇴 회원 관련 409를 화면 동작으로 바꾼다(U2·U6·U8).
 *
 * 세 코드 모두 서버가 그대로 보여줄 수 있는 한국어 메시지를 주므로 `readServerMessage`를
 * 먼저 쓰고, 서버가 아직 코드를 안 내리는 구버전을 위해 프론트 문구를 폴백으로 둔다.
 *
 * 해당 없으면 `null`을 반환해 호출부가 기존 처리를 그대로 쓰게 한다.
 */
export const resolveMembershipLifecycleError = (
    result: SharedScheduleFailure | null | undefined,
): SharePoolAdminError | null => {
    const errorCode = getSharedScheduleErrorCode(result);
    const details = readDetails(result);
    const serverMessage = readServerMessage(result);

    switch (errorCode) {
        case MEMBERSHIP_LIFECYCLE_ENDED_ERROR_CODE: {
            if (details.context === 'CARRIED_OUT') {
                return { errorCode, message: MEMBERSHIP_CARRIED_OUT_MESSAGE, action: 'NONE' };
            }
            // 인계로 종료된 건이면 서버가 갈 곳을 알려준다. 관리자가 "그럼 어디서 하냐"에서 막히지 않게
            // 이동 버튼까지 이어 준다. 회원 삭제·연결 해제(ARCHIVED)는 이어질 수강권이 없다.
            const currentMembershipId = readDetailString(details, 'currentMembershipId');
            return {
                errorCode,
                message: serverMessage || MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE,
                action: currentMembershipId ? 'OPEN_CURRENT_MEMBERSHIP' : 'NONE',
                currentMembershipId: currentMembershipId || undefined,
            };
        }

        // 신규 원매출 환불 API 가 만든 환불행이다. 되돌리는 수단이 아직 없어 재시도가 무의미하다.
        case REFUND_ROW_IMMUTABLE_ERROR_CODE:
            return {
                errorCode,
                message: serverMessage || REFUND_ROW_IMMUTABLE_MESSAGE,
                action: 'CONTACT_SUPPORT',
            };

        // 탈퇴 회원에게도 원매출 환불만은 열려 있다. "전부 막혔다"로 읽히지 않게 예외를 함께 말한다.
        case WITHDRAWN_MEMBER_READ_ONLY_ERROR_CODE:
            return {
                errorCode,
                message: serverMessage || WITHDRAWN_MEMBER_READ_ONLY_MESSAGE,
                action: 'NONE',
            };

        default:
            return null;
    }
};

/**
 * 공유 풀 생성·참여자 관리·한도 변경·종료 실패를 화면 동작으로 바꾼다.
 *
 * 가이드 §4 기준이다. 대부분의 코드는 서버가 그대로 보여줄 수 있는 한국어 메시지를 주므로
 * 메시지를 통과시키고, 후속 동작이 필요한 코드만 action으로 구분한다.
 * 403/404를 나눠 다른 화면을 그리지 않는다 — 서버가 존재 여부를 의도적으로 마스킹한다.
 */
export const resolveSharePoolAdminError = (
    result: SharedScheduleFailure | null | undefined,
    fallback = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해주세요.',
): SharePoolAdminError => {
    const errorCode = getSharedScheduleErrorCode(result);
    const details = readDetails(result);
    const serverMessage = readServerMessage(result);
    const message = serverMessage || fallback;

    // 생명주기 계열(종료된 수강권 / 환불행 / 탈퇴 회원)은 공유와 무관하지만 실패 안내
    // 경로가 이 매퍼 하나로 모여 있다. 별도 매퍼에 두고 여기서 위임해, 어느 화면에서
    // 무엇을 눌러도 같은 문구가 나오게 한다.
    const lifecycleError = resolveMembershipLifecycleError(result);
    if (lifecycleError) return lifecycleError;

    switch (errorCode) {
        case 'SHARE_MEMBER_HAS_FUTURE_USAGE':
            return {
                errorCode,
                message:
                    serverMessage ||
                    '이 회원의 미래 예약과 승인 대기 신청을 먼저 정리해 주세요.',
                action: 'RESOLVE_FUTURE_USAGE',
                memberId: readDetailString(details, 'memberId') || undefined,
            };

        case 'SHARE_POOL_CHANGED':
        case 'SHARE_POOL_IDENTITY_CHANGED':
            return {
                errorCode,
                message:
                    '다른 곳에서 공유 정보가 먼저 변경되었습니다. 최신 상태를 불러왔으니 확인한 뒤 다시 시도해주세요.',
                action: 'REFETCH_POOL',
            };

        case 'MEMBERSHIP_USAGE_BUSY':
            return {
                errorCode,
                message: '다른 요청이 처리 중입니다. 잠시 후 같은 내용으로 다시 시도해주세요.',
                action: details.retryable === true ? 'RETRY_ONCE' : 'NONE',
            };

        // 회원 삭제·연결 해제 경로에서 온다. 소유자를 지우면 참여자 예약이 고아가 되므로
        // 서버가 먼저 막는다. 관리자가 갈 곳(그 공유의 원본 수강권)을 함께 넘긴다.
        case 'SHARE_OWNER_DISCONNECT_BLOCKED':
            return {
                errorCode,
                message:
                    serverMessage ||
                    '공유 수강권 소유자입니다. 공유를 먼저 종료한 뒤 다시 시도해주세요.',
                action: 'CLOSE_OWNED_SHARE_POOLS',
                sourceMembershipIds: readDetailStringList(details, 'sourceMembershipIds'),
            };

        // 한도는 관리자 웹에서만 바꿀 수 있다. 원인만 알리고 끝내면 다음 행동이 없다.
        case 'SHARE_PARTICIPANT_LIMIT':
        case 'SHARE_LIFETIME_PARTICIPANT_LIMIT':
            return {
                errorCode,
                message: serverMessage || '참여 인원 한도를 초과했습니다.',
                action: 'RAISE_PARTICIPANT_LIMIT',
            };

        case 'SHARE_POOL_ALREADY_EXISTS':
            return {
                errorCode,
                message: serverMessage || '이미 활성 공유 수강권이 있습니다.',
                action: 'OPEN_EXISTING_POOL',
                sharePoolId: readDetailString(details, 'sharePoolId') || undefined,
            };

        case 'MEMBERSHIP_NOT_SHAREABLE': {
            const reason = readDetailString(details, 'reason');
            return {
                errorCode,
                message:
                    NOT_SHAREABLE_REASON_MESSAGES[reason] ||
                    serverMessage ||
                    '이 수강권은 공유로 전환할 수 없습니다. 운영 담당자에게 확인해주세요.',
                action: reason === 'MEMBERSHIP_CAPACITY_OVERCOMMITTED' ? 'NONE' : 'CONTACT_SUPPORT',
            };
        }

        case 'SHARE_POOL_PROVISIONING_REVIEW_REQUIRED':
            return {
                errorCode,
                message:
                    serverMessage ||
                    '자동 복구를 계속할 수 없는 상태입니다. 반복 시도하지 말고 운영 담당자에게 확인해주세요.',
                action: 'CONTACT_SUPPORT',
            };

        // 풀과 원본(수강권·그룹 티켓)이 어긋난 무결성 오류라 재시도로 풀리지 않는다.
        case 'SHARE_POOL_MEMBERSHIP_MISMATCH':
            return {
                errorCode,
                message:
                    serverMessage ||
                    '공유 풀과 원본 수강권 정보가 일치하지 않습니다.',
                action: 'CONTACT_SUPPORT',
            };

        // 중단된 공유 환불·업그레이드 작업이 남은 채 **재공유**를 시도한 경우에만 온다.
        // 개인권 최초 전환에는 이 검사가 없다(공유 이력이 없어 작업 자체가 존재할 수 없다).
        //
        // 자동 재시도(RETRY_ONCE)를 걸지 않는다 — 풀 생성은 멱등이 아니고, retryable 은
        // "이 작업이 아직 검토 대기가 아니다"라는 뜻이지 "지금 다시 보내면 통과한다"가 아니다.
        // 사람이 상태를 보고 다시 누르게 둔다.
        case 'SHARE_POOL_ACCOUNTING_OPERATION_IN_PROGRESS': {
            // 서버 메시지는 원인만 말하고 retryable 여부를 담지 않는다. 원인은 서버 문구로
            // 두되 다음 행동은 화면이 붙인다. CONTACT_SUPPORT 는 호출부(buildAdminErrorMessage)가
            // 운영 문의 문장을 덧붙이므로 여기서 같은 말을 반복하지 않는다.
            const cause =
                serverMessage ||
                '같은 수강권의 공유 환불·업그레이드 작업이 아직 완료되지 않았습니다.';
            return details.retryable === true
                ? {
                      errorCode,
                      message: `${cause}\n잠시 후 다시 시도해주세요.`,
                      action: 'NONE',
                  }
                : { errorCode, message: cause, action: 'CONTACT_SUPPORT' };
        }

        case 'SHARED_MEMBERSHIP_DISABLED':
            return {
                errorCode,
                message: '이 센터에서는 공유 수강권 기능을 아직 사용할 수 없습니다.',
                action: 'HIDE_FEATURE',
            };

        case 'ACCESS_TOKEN_REQUIRED':
            return {
                errorCode,
                message: '로그인 정보가 만료되었습니다. 새로고침한 뒤 다시 시도해주세요.',
                action: 'NONE',
            };

        default:
            // 나머지 SHARE_* 코드는 서버 메시지를 그대로 노출한다 (§4 상단 표).
            return { errorCode, message, action: 'NONE' };
    }
};

/**
 * 총 회차 축소 거절(SHARED_MEMBERSHIP_CLOSED_CAPACITY_CONFLICT)의 details를 문구로 만든다.
 * 다른 포매터와 같은 원칙 — 값이 하나라도 손상되면 숫자를 추측하지 않고 빈 문자열을 돌린다.
 */
const formatClosedCapacityConflictDetails = (
    result: SharedScheduleFailure | null | undefined,
): string => {
    const details = readDetails(result);
    const rawValues = [details.occupiedCount, details.shortage];
    if (
        rawValues.some(
            (value) => typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0,
        )
    ) {
        return '';
    }
    const [occupiedCount, shortage] = rawValues as number[];
    return `\n이미 사용·예약된 회차가 ${occupiedCount}회라 ${shortage}회가 모자랍니다.`;
};

/**
 * 이 거절이 **판정 시점**인가 **쓰기 직전**인가(`details.detectedAt`).
 *
 * `PREWRITE` 는 "고를 때는 되던 값이 저장하는 사이에 안 되게 됐다"는 뜻이다 —
 * 그 찰나에 예약이 들어와 하한이 올라갔다. 관리자가 잘못 입력한 게 아니다.
 * 그 사실을 말해주지 않으면 **멀쩡히 고른 값을 자기가 틀린 줄 알고** 헤맨다.
 *
 * ⚠️ 다만 "같은 값으로 다시 하면 된다"는 **아니다.** 하한이 이미 올라갔으므로 같은 값은
 * 또 거절된다. 새 하한(이 응답의 `minimumTotalSession`)을 보고 값을 올리거나, 그 예약이
 * 취소되기를 기다려야 한다. 재시도 소진(MEMBERSHIP_CONTENTION_RETRY_EXHAUSTED)과
 * 헷갈리면 안 된다 — 그쪽은 값이 맞는데 확정을 못 한 것이라 그대로 다시 하면 된다.
 *
 * 모르는 값이거나 없으면 기존 문구를 그대로 쓴다. 값이 늘어도 화면이 깨지지 않는다.
 */
const isPreWriteCapacityConflict = (
    result: SharedScheduleFailure | null | undefined,
): boolean => readDetails(result).detectedAt === 'PREWRITE';

/**
 * 공유 **중** 총 회차 축소 거절(SHARED_MEMBERSHIP_ACTIVE_CAPACITY_CONFLICT)의 details.
 *
 * 종료 후 경로(CLOSED_CAPACITY_CONFLICT)와 **다른 코드**다. 공유 중에는 하한만 막고
 * 나머지는 전부 열려 있어서 할 말이 다르다. 서버가 `minimumTotalSession` 을 직접 주므로
 * 프론트에서 다시 계산하지 않는다 — 조회 스냅샷보다 이쪽이 최신이다.
 *
 * ⚠️ **시작 회차만 바꿔도 이 응답이 온다.** 정원 = 총회차 - 시작회차 + 1 이라 시작 회차를
 * 올리면 정원이 줄어든다. 그래서 문구가 총 회차만 말하면 안 된다.
 */
const formatActiveCapacityConflictDetails = (
    result: SharedScheduleFailure | null | undefined,
): string => {
    const details = readDetails(result);
    const rawValues = [
        details.minimumTotalSession,
        details.usedCount,
        details.futureScheduledCount,
        details.pendingRequestCount,
    ];
    if (
        rawValues.some(
            (value) => typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0,
        )
    ) {
        // 다른 포매터와 같은 원칙 — 값이 하나라도 손상되면 숫자를 추측하지 않는다.
        return '';
    }
    const [minimumTotalSession, usedCount, futureScheduledCount, pendingRequestCount] =
        rawValues as number[];
    const reasons: string[] = [];
    if (usedCount > 0) reasons.push(`이미 사용 ${usedCount}회`);
    if (futureScheduledCount > 0) reasons.push(`예약 ${futureScheduledCount}회`);
    if (pendingRequestCount > 0) reasons.push(`대기 신청 ${pendingRequestCount}건`);
    return `\n${reasons.length > 0 ? `${reasons.join('·')}이 있어요. ` : ''}남길 수 있는 회차는 ${minimumTotalSession}회부터예요.`;
};

/** 수강권 일반 수정(PATCH) 실패를 알럿 제목·본문으로 바꾼다. */
export type SharedMembershipUpdateFailureNotice = {
    title: string;
    message: string;
};

/**
 * 공유 상품 발급 건을 **종료 후 수정**할 때만 나오는 409 두 가지를 문구로 바꾼다.
 *
 * 아는 코드만 다루고 모르면 `null` 을 돌려 기존 처리(권한 412 · 일반 실패 안내)에 위임한다.
 * 이 모듈의 다른 매퍼와 같은 관용구다.
 */
export const resolveSharedMembershipUpdateFailure = (
    result: SharedScheduleFailure | null | undefined,
): SharedMembershipUpdateFailureNotice | null => {
    if (readDetails(result).context === 'CARRIED_OUT') {
        const lifecycleError = resolveMembershipLifecycleError(result);
        if (lifecycleError) return { title: '이동한 수강권이에요', message: lifecycleError.message };
    }
    switch (getSharedScheduleErrorCode(result)) {
        // 발급 원본(sharePolicySnapshot·membershipTemplateId)을 바꾸려 한 경우.
        // 폼은 이 필드를 아예 보내지 않으므로, 이 알럿이 뜨면 그 자체가 폼 버그 신호다.
        case 'SHARED_MEMBERSHIP_ISSUANCE_ORIGIN_IMMUTABLE':
            return {
                title: '변경할 수 없는 항목이에요',
                message:
                    '공유 상품으로 발급된 기록은 변경할 수 없습니다.\n화면을 새로고침한 뒤 다시 시도하고, 계속되면 운영 담당자에게 문의해주세요.',
            };

        // 과거 참여자가 쓴 회차 아래로 총 회차를 줄이려 한 경우.
        // 화면은 sharedUsage.minimumTotalSession 으로 미리 막지만, 하한을 못 받았거나
        // 시작 회차를 함께 바꿨거나 그 사이 예약이 늘면 여기까지 온다.
        case 'SHARED_MEMBERSHIP_CLOSED_CAPACITY_CONFLICT':
            return {
                title: '총 회차를 더 줄일 수 없어요',
                message: `공유 기간에 이미 사용되었거나 예약된 회차보다 적게 줄일 수 없습니다.${formatClosedCapacityConflictDetails(
                    result,
                )}`,
            };

        /**
         * 공유 **중** 정원 하한 위반 — 공유 중 수정에서 서버가 막는 **유일한** 경우다.
         *
         * 시작 회차를 올려서 정원이 줄어든 경우도 여기로 온다. 그래서 "총 회차를 줄일 수
         * 없다"가 아니라 **정원**을 기준으로 말한다.
         */
        case 'SHARED_MEMBERSHIP_ACTIVE_CAPACITY_CONFLICT':
            /**
             * 같은 코드지만 **관리자가 할 일이 갈린다.**
             * - PRECHECK(기본): 처음부터 그 값이 하한 아래였다 → 숫자를 다시 고른다.
             * - PREWRITE: 고를 때는 됐는데 저장하는 사이 예약이 들어왔다 → 값이 틀린 게
             *   아니라 상황이 바뀐 것이다. 새 하한을 보고 다시 고른다.
             */
            return isPreWriteCapacityConflict(result)
                ? {
                      title: '저장하는 사이에 예약이 잡혔어요',
                      message: `값을 고른 뒤에 이 수강권에 예약이 새로 들어왔어요.${formatActiveCapacityConflictDetails(
                          result,
                      )}\n입력이 잘못된 게 아니에요 — 위 기준으로 회차를 다시 정해주세요.`,
                  }
                : {
                      title: '회차를 더 줄일 수 없어요',
                      message: `이미 사용되었거나 예약된 회차보다 적게 줄일 수 없어요.${formatActiveCapacityConflictDetails(
                          result,
                      )}\n예약이 취소된 뒤라면 화면을 새로고침한 뒤 다시 시도해주세요.`,
                  };

        /**
         * 동시 수정. **자동 재시도하지 않는다.**
         *
         * 조회한 정원 위에서만 저장이 성립하도록 서버가 조건부 갱신으로 묶은 것이라,
         * 같은 값으로 다시 보내면 방금 들어온 예약을 정원 밖으로 밀어낼 수 있다.
         * 관리자 웹은 **사람이 다시 보고 판단**하게 한다 — 자동 재시도·자동 저장을 넣지 않는다.
         */
        case 'MEMBERSHIP_CONCURRENT_MODIFICATION':
            return {
                title: '다른 곳에서 먼저 바뀌었어요',
                message:
                    '이 수강권이 방금 다른 곳에서 변경되었어요.\n화면을 새로고침해 지금 값을 확인한 뒤 다시 수정해주세요.',
            };

        /**
         * 서버가 재시도를 다 쓰고도 경합이 계속될 때. **하한 위반과 다른 실패다.**
         *
         * 하한 위반(`..._ACTIVE_CAPACITY_CONFLICT`)은 "이 값으로는 줄일 수 없다"는 판정이라
         * 관리자가 **숫자를 바꿔야** 하지만, 이쪽은 "지금 예약이 계속 들어와 확정하지
         * 못했다"라 **값을 그대로 두고 잠시 뒤 다시 하면 된다.** 두 문구가 같으면
         * 관리자가 멀쩡한 값을 엉뚱하게 고치게 된다.
         *
         * **자동 재시도를 넣지 않는다.** 서버가 이미 백오프까지 써서 재시도를 소진한
         * 상태다. 화면이 한 번 더 즉시 보내봐야 같은 결과이고, 그 사이 다른 관리자가
         * 무엇을 바꿨는지도 알 수 없다. 사람이 잠시 뒤 다시 누르게 한다.
         * (근거: 백엔드 `membership.controller.ts` `retryOnConcurrentModification`)
         */
        case 'MEMBERSHIP_CONTENTION_RETRY_EXHAUSTED':
            return {
                title: '잠시 후 다시 시도해주세요',
                message:
                    '지금 이 수강권에 예약이 계속 들어오고 있어 저장을 확정하지 못했어요.\n입력한 값은 그대로 두고 잠시 후 다시 시도하면 됩니다.',
            };

        default:
            return null;
    }
};
